import type { ComponentProps, ReactNode } from "react";
import { Animated, type StyleProp, View, type ViewStyle } from "react-native";

import { BrowseFilterControl } from "@/components/browse/BrowseFilterControl";
import { BrowseSearchBar } from "@/components/browse/BrowseSearchBar";
import { BrowseViewToggle } from "@/components/browse/BrowseViewToggle";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { Screen } from "@/components/ui/Screen";
import type { BrowseLayout } from "@/hooks/usePublicBrowseView";

export type StorefrontListShellProps = {
  testID: string;
  headerTestID: string;
  /** From `useStorefrontGrid`. */
  maxContentWidth: number;
  /** The caller's `useCollapsingHeader`; the caller also spreads its `collapsible` and `list`. */
  collapse: {
    cover: StyleProp<ViewStyle>;
    header: Pick<ComponentProps<typeof Animated.View>, "onLayout" | "style">;
  };
  /** The floating header's content: the identity that scrolls away, then the pinned controls. */
  header: ReactNode;
  /** The list or its empty state, and anything that floats over it. */
  children: ReactNode;
};

/**
 * The page frame of a public storefront list, shared by the shop (`inventory/[slug]`) and a
 * table's Pull Sheet browse (`browse/[eventId]`): capped, centered content under a header whose
 * identity scrolls away while search and filters stay pinned (TRO-905).
 */
export function StorefrontListShell(
  { testID, headerTestID, maxContentWidth, collapse, header, children }: StorefrontListShellProps,
) {
  return (
    <Screen testID={testID}>
      {/* This screen sets no per-page `<title>` for the tab or link preview. */}
      {
        /* Full-bleed background with capped, centered content. `role="main"` gives screen readers a
        `main` landmark, which react-native-web does not render by default (#531). */
      }
      <View className="w-full flex-1 self-center" role="main" style={{ maxWidth: maxContentWidth }}>
        <View aria-hidden className="bg-bg" style={collapse.cover} />
        <Animated.View testID={headerTestID} {...collapse.header}>
          {header}
        </Animated.View>
        {children}
      </View>
    </Screen>
  );
}

export type StorefrontToolbarProps = {
  query: string;
  onQueryChange: (query: string) => void;
  /** The Filters sheet: its facets, the count its action shows and what Clear all resets. */
  filters: ComponentProps<typeof BrowseFilterControl>;
  layout: BrowseLayout;
  onLayoutChange: (layout: BrowseLayout) => void;
  /** The row under the search: sort, the caller's quick filters and the applied-filter chips. */
  children: ReactNode;
};

/** A public storefront's pinned search row (search, Filters, Grid/List) above the caller's row. */
export function StorefrontToolbar(
  { query, onQueryChange, filters, layout, onLayoutChange, children }: StorefrontToolbarProps,
) {
  return (
    <View className="gap-2">
      <View className="flex-row items-center gap-2">
        <View className="flex-1">
          <BrowseSearchBar value={query} onChange={onQueryChange} />
        </View>
        <BrowseFilterControl {...filters} />
        <BrowseViewToggle layout={layout} onChange={onLayoutChange} />
      </View>
      {children}
    </View>
  );
}

/** A public storefront that failed to load, with a retry. */
export function StorefrontLoadError(
  { title, subtitle, onRetry }: { title: string; subtitle: string; onRetry: () => void; },
) {
  return (
    // Keep the `main` landmark in the error state too, since a stale link or QR lands here (#531).
    <Screen role="main" accessibilityLiveRegion="assertive">
      <EmptyState
        mark
        title={title}
        subtitle={subtitle}
        action={
          <Button
            title="Try again"
            accessibilityLabel="Try again"
            variant="outline"
            onPress={onRetry}
          />
        }
      />
    </Screen>
  );
}
